import React, { Fragment, useState } from "react";
import CurrencyInput from "../CurrencyInput";
import {
  calculatePurchaseLine,
  getPurchaseLineKey,
} from "../../utils/purchaseReceiving";

const formatCurrency = (value) => Number(value || 0).toLocaleString("en-US", {
  minimumFractionDigits: Number(value) % 1 ? 2 : 0,
  maximumFractionDigits: 2,
});

export default function PurchaseLineTable({
  lines,
  lineErrors = {},
  onUpdate,
  onRemove,
}) {
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleDetails = (lineKey) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(lineKey)) next.delete(lineKey);
      else next.add(lineKey);
      return next;
    });
  };

  return (
    <div className="purchase-lines-table-wrap">
      <table className="purchase-lines-table">
        <thead>
          <tr>
            <th scope="col">Product / unit</th>
            <th scope="col">Quantity</th>
            <th scope="col">Buying price</th>
            <th scope="col">Selling price</th>
            <th scope="col">Line total</th>
            <th scope="col"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const lineKey = getPurchaseLineKey(line);
            const calculation = calculatePurchaseLine(line);
            const isExpanded = expanded.has(lineKey);
            const lineError = lineErrors[lineKey];
            return (
              <Fragment key={lineKey}>
                <tr
                  className={`purchase-line-row ${lineError ? "purchase-line-invalid" : ""}`}
                  data-purchase-line={lineKey}
                >
                  <th scope="row" data-label="Product / unit">
                    <div className="purchase-line-product">
                      <span className="purchase-line-state">
                        {line.isNewItem ? "NEW ITEM" : "MATCHED ITEM"}
                      </span>
                      <strong>{line.itemName}</strong>
                      <span>1 {line.unitName} = {line.conversionFactor} base unit{Number(line.conversionFactor) === 1 ? "" : "s"}</span>
                      <small>{line.barcode || "Generated barcode"}</small>
                      {lineError && (
                        <span className="purchase-line-error" role="alert">
                          {lineError}
                        </span>
                      )}
                      {calculation.warning && (
                        <span className="purchase-price-warning" role="status">
                          {calculation.warning}
                        </span>
                      )}
                    </div>
                  </th>
                  <td data-label="Quantity">
                    <label className="sr-only" htmlFor={`${lineKey}-quantity`}>
                      Quantity for {line.itemName} in {line.unitName}
                    </label>
                    <input
                      id={`${lineKey}-quantity`}
                      name="quantity"
                      type="number"
                      min="1"
                      step="1"
                      inputMode="numeric"
                      value={line.quantity}
                      onChange={(event) => onUpdate(lineKey, "quantity", event.target.value)}
                    />
                  </td>
                  <td data-label="Buying price">
                    <CurrencyInput
                      id={`${lineKey}-buy`}
                      name="unitBuyPrice"
                      value={line.unitBuyPrice}
                      onChange={(value) => onUpdate(lineKey, "unitBuyPrice", value)}
                      min={0}
                      step={100}
                      label={`${line.itemName} buying price per ${line.unitName}`}
                    />
                  </td>
                  <td data-label="Selling price">
                    <CurrencyInput
                      id={`${lineKey}-sell`}
                      name="unitSellPrice"
                      value={line.unitSellPrice}
                      onChange={(value) => onUpdate(lineKey, "unitSellPrice", value)}
                      min={0}
                      step={100}
                      label={`${line.itemName} selling price per ${line.unitName}`}
                    />
                  </td>
                  <td className="purchase-line-total" data-label="Line total">
                    {Object.keys(calculation.errors).length
                      ? "—"
                      : `MMK ${formatCurrency(calculation.lineTotal)}`}
                  </td>
                  <td className="purchase-line-actions" data-label="Actions">
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      data-line-details-toggle={lineKey}
                      aria-expanded={isExpanded}
                      onClick={() => toggleDetails(lineKey)}
                    >
                      {isExpanded ? "Hide details" : "Details"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => onRemove(lineKey)}
                      aria-label={`Remove ${line.itemName}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr className="purchase-line-details-row">
                    <td colSpan="6">
                      <div className="purchase-line-calculation" aria-label={`${line.itemName} calculation`}>
                        {Object.keys(calculation.errors).length ? (
                          <p className="purchase-line-helper">Enter quantity, buying price, and selling price to see the calculation.</p>
                        ) : (
                          <>
                            <dl>
                              <div><dt>Stock added</dt><dd>{calculation.receivedBaseQuantity} base units</dd></div>
                              <div><dt>Base buying price</dt><dd>MMK {formatCurrency(calculation.baseBuyPrice)}</dd></div>
                              <div><dt>Base selling price</dt><dd>MMK {formatCurrency(calculation.baseSellPrice)}</dd></div>
                              <div><dt>Margin</dt><dd>{formatCurrency(calculation.marginPercent)}%</dd></div>
                              <div className="is-total"><dt>Line total</dt><dd>MMK {formatCurrency(calculation.lineTotal)}</dd></div>
                            </dl>
                            <p>The base selling price will proportionally update all active packaging-unit selling prices.</p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
